import { useState } from "react";

import { IconButton } from "@similarweb/ui-components";

import type { IFlattenedCategory } from "app/common/services/categoryService.types";
import { translate } from "app/filters/i18nFilter";
import {
    IndustryDataAvailabilityPageBanner,
    renderIndustryDataAvailabilityNotice,
} from "app/pages/digital-marketing/ad-intelligence/common/data-availability/IndustryDataAvailabilityBanner";
import { useDisplayAdvertiserPublisherDataAndParams } from "app/pages/digital-marketing/ad-intelligence/common/hooks/useDisplayAdvertiserPublisherDataAndParams";
import { FIRST_PAGE } from "app/pages/digital-marketing/ad-intelligence/common/utils/utils";
import { allTrackers } from "app/services/track/track";
import { displayAdvertisingNavigation } from "single-spa/react-app/config/solutions25/adIntelligence/displayAdvertising/displayAdvertisingNavigator";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { TOP_ADS_TABLE_ID } from "../overview/RecentAdsGallery";

import { AdsGallery } from "./AdsGallery";
import { INDUSTRY_SORT_OPTIONS } from "./adsSortOptions";
import type { IndustrySortType } from "./adsSortOptions";
import { IndustryAdsFilters } from "./industry/IndustryAdsFilters";
import type { IndustryCreativeType } from "./industry/IndustryAdsFormatFilter";
import { isIndustryCreativeType } from "./industry/IndustryAdsFormatFilter";
import { useIndustryAdsGallery } from "./industry/useIndustryAdsGallery";

const NUMBER_OF_ADS_PER_PAGE = 100;
const OVERVIEW_ADS_COUNT = 10;

interface IIndustryAdsProps {
    category: IFlattenedCategory;
    isOverviewContext?: boolean;
}

export const IndustryAds = ({ category, isOverviewContext = false }: IIndustryAdsProps) => {
    const params = useDisplayAdvertiserPublisherDataAndParams();
    const [currentPage, setCurrentPage] = useState(FIRST_PAGE);
    const [sort, setSort] = useState<IndustrySortType>("last_seen");
    // `selectedCampaigns` is a declared ads-state param: string[] at runtime (or undefined).
    const [selectedCampaigns, setSelectedCampaigns] = useState<string[]>(params.selectedCampaigns ?? []);
    // `selectedAdvertisers` is a declared ads-route param (string[] at runtime) - hydrated from the URL.
    const [selectedAdvertisers, setSelectedAdvertisers] = useState<string[]>(params.selectedAdvertisers ?? []);
    // `selectedPublishers` is a declared ads-route param (string[] at runtime) - hydrated from the URL.
    const [selectedPublishers, setSelectedPublishers] = useState<string[]>(params.selectedPublishers ?? []);
    // `format` is a single string param on the ads URL (never an array) - narrow via type guard.
    const [selectedFormat, setSelectedFormat] = useState<IndustryCreativeType | undefined>(
        isIndustryCreativeType(params.format) ? params.format : undefined,
    );

    const pageSize = isOverviewContext ? OVERVIEW_ADS_COUNT : NUMBER_OF_ADS_PER_PAGE;
    const page = isOverviewContext ? FIRST_PAGE : currentPage;
    // The overview widget hides the filter chips, so it must not apply any
    // URL-hydrated filters — the Recent Ads preview always shows the full set.
    const { data, isLoading, isError, totalCount, availability } = useIndustryAdsGallery(
        category,
        page,
        pageSize,
        sort,
        isOverviewContext
            ? undefined
            : {
                  campaignPages: selectedCampaigns,
                  advertisers: selectedAdvertisers,
                  publishers: selectedPublishers,
                  creativeTypes: selectedFormat ? [selectedFormat] : undefined,
              },
    );

    const resetPage = () => {
        setCurrentPage(FIRST_PAGE);
    };

    const handleCampaignsFilterChange = (campaigns: string[]) => {
        setSelectedCampaigns(campaigns);
        resetPage();
        if (campaigns.length) {
            allTrackers.dispatchTrackEvent("Drop Down", "click", `Filter Campaign/${campaigns.length} selected`);
        }
        void proNavigator.updateParams({ ...params, selectedCampaigns: campaigns }, { reload: false });
    };

    const handleAdvertisersFilterChange = (advertisers: string[]) => {
        setSelectedAdvertisers(advertisers);
        resetPage();
        if (advertisers.length) {
            allTrackers.dispatchTrackEvent("Drop Down", "click", `Filter Advertiser/${advertisers.length} selected`);
        }
        void proNavigator.updateParams({ ...params, selectedAdvertisers: advertisers }, { reload: false });
    };

    const handlePublishersFilterChange = (publishers: string[]) => {
        setSelectedPublishers(publishers);
        resetPage();
        if (publishers.length) {
            allTrackers.dispatchTrackEvent("Drop Down", "click", `Filter Publisher/${publishers.length} selected`);
        }
        void proNavigator.updateParams({ ...params, selectedPublishers: publishers }, { reload: false });
    };

    const handleFormatFilterChange = (format?: IndustryCreativeType) => {
        setSelectedFormat(format);
        resetPage();
        void proNavigator.updateQueryParams({ format }, { reload: false });
    };

    const handleSeeAll = () => {
        allTrackers.dispatchTrackEvent("Internal Link", "click", "Advertiser_overview/Top_ads/see_more");
        void displayAdvertisingNavigation.ads.go(params);
    };

    return (
        <div id={isOverviewContext ? TOP_ADS_TABLE_ID : undefined}>
            {/* On the full Ads page the notice is page furniture, so it sits above the
                gallery card next to `MixedScrapperBanner`. In the Overview widget the
                same notice belongs to the widget, so it goes in the gallery's slot. */}
            {isOverviewContext ? null : <IndustryDataAvailabilityPageBanner availability={availability} />}
            <AdsGallery
                data={data}
                isLoading={isLoading}
                isError={isError}
                totalCount={totalCount}
                controller="advertiser"
                industryName={category.text}
                sortOptions={INDUSTRY_SORT_OPTIONS}
                selectedSort={sort}
                onSortChange={(id) => {
                    setSort(id as IndustrySortType);
                    setCurrentPage(FIRST_PAGE);
                }}
                currentPage={page}
                onPageChange={setCurrentPage}
                titleLabelKey="ad.intelligence.advertiser.ad.widget.title"
                showAdvertiserColumn={true}
                noticeSlot={isOverviewContext ? renderIndustryDataAvailabilityNotice(availability) : undefined}
                filtersSlot={
                    isOverviewContext ? undefined : (
                        <IndustryAdsFilters
                            category={category}
                            selectedCampaigns={selectedCampaigns}
                            onCampaignsFilterChange={handleCampaignsFilterChange}
                            selectedAdvertisers={selectedAdvertisers}
                            onAdvertisersFilterChange={handleAdvertisersFilterChange}
                            selectedPublishers={selectedPublishers}
                            onPublishersFilterChange={handlePublishersFilterChange}
                            selectedFormat={selectedFormat}
                            onFormatFilterChange={handleFormatFilterChange}
                        />
                    )
                }
                footerSlot={
                    isOverviewContext ? (
                        <IconButton
                            type="flat"
                            onClick={handleSeeAll}
                            iconName="arrow-right"
                            placement="right"
                            iconSize="xs"
                        >
                            {translate("ad.intelligence.overview.top.ads.see.all.button")}
                        </IconButton>
                    ) : undefined
                }
            />
        </div>
    );
};
